"use client";

import { Link } from "@/components/LocaleLink";
import { useTranslation } from "@/lib/hooks/useTranslation";
import {
  FileSearch,
  Mic,
  Briefcase,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

type ToolKey = "ats" | "interview-tech" | "interview-finance" | "interview-consulting";

interface ToolCard {
  key: ToolKey;
  icon: LucideIcon;
  href: string;
  titleFr: string;
  titleEn: string;
  descFr: string;
  descEn: string;
  kickerFr: string;
  kickerEn: string;
}

const TOOLS: Record<ToolKey, ToolCard> = {
  ats: {
    key: "ats",
    icon: FileSearch,
    href: "/analyse-cv",
    titleFr: "Analyse ATS de ton CV",
    titleEn: "ATS resume analysis",
    descFr: "Score sur 100 en 30 secondes. Gratuit, sans compte.",
    descEn: "100-point score in 30 seconds. Free, no signup.",
    kickerFr: "GRATUIT",
    kickerEn: "FREE",
  },
  "interview-tech": {
    key: "interview-tech",
    icon: Mic,
    href: "/entretien-tech",
    titleFr: "Entraînement entretien tech",
    titleEn: "Tech interview prep",
    descFr: "Algos, système, comportemental — style FAANG + scale-ups FR.",
    descEn: "Algorithms, system design, behavioral — FAANG + FR scale-ups.",
    kickerFr: "SIMULATION",
    kickerEn: "SIMULATION",
  },
  "interview-finance": {
    key: "interview-finance",
    icon: Briefcase,
    href: "/entretien-finance",
    titleFr: "Entretien finance",
    titleEn: "Finance interview prep",
    descFr: "DCF, LBO, brain teasers — IB, PE, M&A, Corp Fin.",
    descEn: "DCF, LBO, brain teasers — IB, PE, M&A, Corp Fin.",
    kickerFr: "SIMULATION",
    kickerEn: "SIMULATION",
  },
  "interview-consulting": {
    key: "interview-consulting",
    icon: TrendingUp,
    href: "/entretien-consulting",
    titleFr: "Entretien consulting",
    titleEn: "Consulting interview prep",
    descFr: "Cases, market sizing, M&A — style MBB + Big Four.",
    descEn: "Cases, market sizing, M&A — MBB + Big Four style.",
    kickerFr: "SIMULATION",
    kickerEn: "SIMULATION",
  },
};

interface ToolsCTAProps {
  tools?: ToolKey[];
  heading?: { fr: string; en: string };
  subheading?: { fr: string; en: string };
}

export function ToolsCTA({
  tools = ["ats", "interview-tech", "interview-finance", "interview-consulting"],
  heading,
  subheading,
}: ToolsCTAProps) {
  const { locale } = useTranslation();
  const isFr = locale === "fr";

  const titleText = heading
    ? isFr
      ? heading.fr
      : heading.en
    : isFr
      ? "Les outils Velyq pour passer à l'action"
      : "Velyq tools to take action";

  const subtitleText = subheading
    ? isFr
      ? subheading.fr
      : subheading.en
    : isFr
      ? "Un écosystème connecté — chaque outil appelle les autres."
      : "A connected ecosystem — each tool feeds the others.";

  return (
    <section className="py-16 bg-muted/30 border-t">
      <div className="container mx-auto px-4">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold sm:text-3xl">{titleText}</h2>
            <p className="mt-2 text-muted-foreground">{subtitleText}</p>
          </div>
          <div className={`grid gap-4 sm:grid-cols-2 ${tools.length >= 3 ? "lg:grid-cols-4" : "lg:grid-cols-2"}`}>
            {tools.map((key) => {
              const tool = TOOLS[key];
              const Icon = tool.icon;
              return (
                <Link
                  key={tool.key}
                  href={tool.href}
                  className="group flex flex-col rounded-xl border bg-card p-5 transition-all hover:shadow-lg hover:border-primary/50"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="rounded-lg bg-primary/10 p-2.5 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold tracking-wider text-primary">
                      {isFr ? tool.kickerFr : tool.kickerEn}
                    </span>
                  </div>
                  <h3 className="font-semibold group-hover:text-primary transition-colors">
                    {isFr ? tool.titleFr : tool.titleEn}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {isFr ? tool.descFr : tool.descEn}
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
